import store from 'redux/store';
import { deleteConnection } from 'redux/slices/connectionsSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditConnection } from './edit/showEditConnection';

type ConnectionEntity = Extract<ActionEntity, { type: 'connection' }>;

const isAllConnection = (entities: readonly ActionEntity[]): entities is readonly ConnectionEntity[] =>
  entities.every((entity) => entity.type === 'connection');

/**
 * Action definitions for connections. `createConnection` /
 * `addToTransportConnection` are scoped; `edit` (single-only) and `delete` are
 * contextual. `deleteConnection` is keyed by name. delete now confirms via the
 * shared executor (the old row/toolbar delete deleted immediately, unconfirmed).
 */
export const connectionActionDefinitions: ActionDefinitions = [
  {
    id: 'createConnection',
    scope: 'type',
    label: 'New Connection',
    resolve: (ctx) => () => void showEditConnection({ mode: 'create', createIn: ctx.createIn }).then(ctx.refresh),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllConnection(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditConnection({ mode: 'edit', id: only.id }).then(ctx.refresh);
    },
  },
  {
    id: 'delete',
    label: 'Remove',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllConnection(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      const single = entities.length === 1 ? entities[0] : null;
      return deleteExecutor(entities, (entity) => store.dispatch(deleteConnection(entity.name)), {
        refresh,
        confirmBody: single
          ? `Are you sure you want to delete the connection "${single.name}"?`
          : `Are you sure you want to delete these ${entities.length} connections?`,
      });
    },
  },
  {
    id: 'addToTransportConnection',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllConnection(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'connection',
        entities.map((entity) => entity.id),
        { objectLabel: single ? `${single.name} (${single.connectionType})` : 'Selected connections' }
      );
    },
  },
];
